import { html, TemplateResult } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { Week } from '../../../api/get-calendar-data-for-house';
import { formatNumberWithoutDecimals } from '../../../helpers/number';
import { Dispatcher } from '../../../types/booking-request';

export function calendarDay(
	startOfStay: Date | null,
	endOfStay: Date | null,
	houseId: number,
	dispatch: Dispatcher,
	date: Date,
	weekSaturday: Date,
	week: Week
): TemplateResult {
	// If the week cannot be booked or the saturday of that week is in the past, this day should be greyed out.
	if (
		!week.priceInEuroCents ||
		'NOT_AVAILABLE' === week.availability ||
		new Date() > weekSaturday
	) {
		const dayClasses = {
			'calendar__day--selected': (
				null !== startOfStay &&
				null !== endOfStay &&
				date > startOfStay &&
				date < endOfStay
			),
			'calendar__day--selected-end': (
				null !== endOfStay &&
				date.getTime() === endOfStay.getTime()
			),
		};

		return html`
			<div class="calendar__day calendar__day--not-available ${classMap( dayClasses )}">
				${date.getUTCDate()}
			</div>
		`;
	}

	// If the date is not saturday, just return the day.
	if ( 6 !== date.getUTCDay() ) {
		const dayClasses = {
			'calendar__day--selected': (
				null !== startOfStay &&
				null !== endOfStay &&
				date > startOfStay &&
				date < endOfStay
			),
			'calendar__day--is-booked': 'IS_BOOKED' === week.availability,
		};

		return html`
			<div class="calendar__day ${classMap( dayClasses )}">
				${date.getUTCDate()}
			</div>
		`;
	}

	const clickHandler = () => {
		if ( 'HAS_OPTION' === week.availability ) {
			dispatch( {
				type: 'UPDATE_START_OF_STAY',
				startOfStay: date,
			} );

			return;
		}

		dispatch( {
			type: 'UPDATE_START_OF_STAY',
			startOfStay: date,
		} );
	};

	const mouseEnterHandler = ( e: MouseEvent ) => {
		if ( !( e.target instanceof HTMLElement ) ) {
			return;
		}

		const tooltipEl = e.target.nextSibling;
		if (
			!tooltipEl ||
			!( tooltipEl instanceof HTMLElement )
		) {
			return;
		}

		const x = Math.round( e.clientX - e.target.getBoundingClientRect().left + 20 );
		const y = Math.round( e.clientY - e.target.getBoundingClientRect().top + 10 );

		tooltipEl.style.display = 'block';
		tooltipEl.style.transform = `translate3d(${x}px, ${y}px, 0)`;
	};

	const mouseMoveHandler = ( e: MouseEvent ) => {
		if ( !( e.target instanceof HTMLElement ) ) {
			return;
		}

		const tooltipEl = e.target.nextSibling;
		if (
			!tooltipEl ||
			!( tooltipEl instanceof HTMLElement )
		) {
			return;
		}

		let x = Math.round( e.clientX - e.target.getBoundingClientRect().left + 20 );
		const y = Math.round( e.clientY - e.target.getBoundingClientRect().top + 10 );

		/* Place tooltips on the left on mobile screens so they don't overflow. */
		if ( matchMedia( '(max-width: 767px)' ).matches ) {
			x = Math.round( e.clientX - e.target.getBoundingClientRect().right - 20 );
		}

		tooltipEl.style.transform = `translate3d(${x}px, ${y}px, 0)`;
	};

	const mouseLeaveHandler = ( e: MouseEvent ) => {
		if ( !( e.target instanceof HTMLElement ) ) {
			return;
		}

		const tooltipEl = e.target.nextSibling;
		if (
			!tooltipEl ||
			!( tooltipEl instanceof HTMLElement )
		) {
			return;
		}

		tooltipEl.style.display = 'none';
	};

	// From here on, we know this day needs a button.
	const dayClasses = {
		'calendar__day--selected-start': (
			null !== startOfStay &&
			date.getTime() === startOfStay.getTime()
		),
		'calendar__day--selected': (
			null !== startOfStay &&
			null !== endOfStay &&
			date > startOfStay &&
			date < endOfStay
		),
		'calendar__day--selected-end': (
			null !== endOfStay &&
			date.getTime() === endOfStay.getTime()
		),
		'calendar__day--is-booked': 'IS_BOOKED' === week.availability,
	};

	const triggerClasses = {
		'calendar__trigger--has-option': 'HAS_OPTION' === week.availability,
	};

	return html`
		<div class="calendar__day ${classMap( dayClasses )}">
			<button
				.disabled=${'IS_BOOKED' === week.availability}
				@click=${clickHandler}
				@mouseenter=${mouseEnterHandler}
				@mouseleave=${mouseLeaveHandler}
				@mousemove=${mouseMoveHandler}
				class="calendar__trigger ${classMap( triggerClasses )}"
			>
				<span class="calendar__trigger__circle">
					${date.getUTCDate()}
				</span>
			</button><span class="calendar__day__tool-tip">
				${formatPrice( week.priceInEuroCents )}
			</span>
		</div>
	`;
}

function formatPrice( price: number | undefined ): TemplateResult {
	if ( !price ) {
		return html``;
	}

	return html`€${formatNumberWithoutDecimals( price )},- / 7 nights`;
}
